import { Box } from '@chakra-ui/react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  CalibrationStatus,
  updateCalibrationStatus,
} from '../../ReviewManage/components/ReviewManageTable/components/BulkActions/dataHooks';
import { useTargetReviewCycleId } from '../../ReviewManage/components/ReviewManageTable/components/ShareResultsDrawer/dataHooks';
import { useUpdateReviewManageSingleRowData } from '../../ReviewManage/reviewManageSidepanel/dataHooks';
import { useUpdateCalibrationViewSingleRowData } from '../CalibrationViewSidepanel/dataHooks';
import {
  CALIBRATION_STATUS_COLORS,
  CALIBRATION_STATUS_LABELS,
  CALIBRATION_STATUS_VALUES,
} from '../columnDefinitionHelpers';
import { useCalibrationViewLandingQueryParams } from '../helpers';

type CalibrationStatusCellRendererProps = {
  status: keyof typeof CALIBRATION_STATUS_VALUES | null;
  employeeId: number | null;
};

export const CalibrationStatusCellRenderer = ({ status, employeeId }: CalibrationStatusCellRendererProps) => {
  const history = useHistory();
  const toast = useToast();
  const queryClient = useQueryClient();
  const reviewCycleId = useTargetReviewCycleId();
  const selectedCohort = useSelectedView();
  const { calibrationViewId } = useCalibrationViewLandingQueryParams();
  const updateReviewManageSingleRowData = useUpdateReviewManageSingleRowData();
  const updateCalibrationViewSingleRowData = useUpdateCalibrationViewSingleRowData();

  if (!status) return <TextBodyMedium>NA</TextBodyMedium>;

  const selectedEmployeeId = employeeId ? [employeeId] : [];
  const statusLabel = CALIBRATION_STATUS_LABELS[status];
  const statusColor = CALIBRATION_STATUS_COLORS[status];

  const handleCalibrationStatusUpdate = async (selectedStatus: CalibrationStatus) => {
    if (selectedEmployeeId.length === 0) {
      toast({ title: 'Error in updating calibration status. Please try again.', status: 'error' });
      return;
    }

    // update calibration status using api
    try {
      await updateCalibrationStatus({
        employeeIds: selectedEmployeeId,
        calibrationStatus: selectedStatus,
        reviewCycleId: +reviewCycleId,
      });
      toast({ title: 'Calibration status updated successfully', status: 'success' });
    } catch (err) {
      console.error('Error in updating calibration status ', err);
      toast({ title: 'Error in updating calibration status. Please try again.', status: 'error' });
    }

    // refresh table and metric card
    const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
    if (isTalentPlanningPage) {
      updateCalibrationViewSingleRowData({
        calibrationViewId,
        empId: selectedEmployeeId,
        cohortDetails: selectedCohort,
      });
      // await queryClient.invalidateQueries([
      //   REACT_QUERY_KEYS.talentPlanning.tabularData,
      //   selectedCohort?.id,
      //   calibrationViewId,
      // ]);
    } else {
      // await queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
      updateReviewManageSingleRowData({
        reviewCycleId,
        empId: selectedEmployeeId,
        cohortDetails: selectedCohort,
      });
      await queryClient.invalidateQueries([
        REACT_QUERY_KEYS.reviewAiCalibrationMetrics,
        selectedCohort?.id,
        reviewCycleId,
      ]);
    }
  };

  return (
    <Box className="info">
      {statusLabel && (
        <Select value={status} onValueChange={(value: CalibrationStatus) => handleCalibrationStatusUpdate(value)}>
          <SelectTrigger style={{ width: '140px' }}>
            <TagColored color={statusColor}>{statusLabel}</TagColored>
          </SelectTrigger>
          <SelectContent style={{ width: '150px' }}>
            <SelectGroup>
              {Object.keys(CALIBRATION_STATUS_LABELS).map((key) => (
                <SelectItem key={key} value={key}>
                  {CALIBRATION_STATUS_LABELS[key]}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    </Box>
  );
};
